import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import React, { FunctionComponent, useEffect, useRef } from 'react';
import { TextExpanderToggle } from './TextExpanderToggle/TextExpanderToggle';
import { useIsSingleLine } from './useIsSingleLine';

import './text-expander.scss';

export interface TextExpanderProps {
    calculateOnResize?: boolean;
    children: React.ReactNode;
    className?: string;
    collapsedViewLines?: number;
    expandedViewLines?: number;
    expanded: boolean;
    lineHeight?: string /* this must be on ex units */;
    onTextNumberLinesCalculated?: (numberOfLines: number) => void;
    onToggle: (isExpanded: boolean) => void;
    readLessText?: string;
    readMoreText?: string;
    toggleClassName?: string;
}

export const TextExpander: FunctionComponent<TextExpanderProps> = ({
    calculateOnResize = false,
    children,
    className,
    collapsedViewLines = 1,
    expandedViewLines = 6,
    expanded = false,
    onTextNumberLinesCalculated,
    onToggle,
    readLessText = gettext('Read less'),
    readMoreText = gettext('Read more'),
    toggleClassName,
    lineHeight = '2.5ex',
}) => {
    if (typeof children !== 'string') {
        throw new Error('Children must be a string');
    }

    const contentRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<string>('');

    const styles = {
        '--collapsedViewLines': collapsedViewLines,
        '--expandedViewLines': expandedViewLines,
        '--lineHeight': lineHeight,
    } as React.CSSProperties;

    useEffect(() => {
        if (!contentRef.current) {
            return;
        }

        textRef.current = children;
    }, [children]);

    const { isSingleLine, isResizing, numberOfLines } = useIsSingleLine(
        contentRef,
        textRef,
    );

    /*
        if we expanded and then resize making isSingleLine true, then collapse automatically
    */
    useEffect(() => {
        if (calculateOnResize && expanded && isSingleLine && !isResizing) {
            onToggle(!expanded);
        }
    }, [isSingleLine, expanded, isResizing, onToggle, calculateOnResize]);

    useEffect(() => {
        onTextNumberLinesCalculated?.(numberOfLines);
    }, [numberOfLines, onTextNumberLinesCalculated]);

    const { readLess, readMore, expanderClass } = expanded
        ? {
              expanderClass: 'eds-text-expander--expanded',
              readLess: isSingleLine ? null : (
                  <p className="eds-text-expander-read-less">
                      <TextExpanderToggle
                          className={toggleClassName}
                          text={readLessText}
                          onClick={() => {
                              onToggle(!expanded);
                              if (contentRef && contentRef.current) {
                                  contentRef.current.scrollTop = 0;
                              }
                          }}
                      />
                  </p>
              ),
              readMore: null,
          }
        : {
              expanderClass: 'eds-text-expander--collapsed',
              readLess: null,
              readMore: isSingleLine ? null : (
                  <TextExpanderToggle
                      className={toggleClassName}
                      text={readMoreText}
                      onClick={() => onToggle(!expanded)}
                      ariaExpanded={expanded}
                  />
              ),
          };

    return (
        <div
            style={styles}
            className={classNames('eds-text-expander', className)}
            data-testid="eds-textExpander"
        >
            <div
                ref={contentRef}
                className={classNames(
                    'eds-text-expander-content',
                    expanderClass,
                )}
            >
                {children}
                {readLess}
            </div>
            {readMore}
        </div>
    );
};
